import React, { useState, useEffect, useRef } from "react";
import { API } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, X, Heart, AlertTriangle, Sparkles, Trophy } from "lucide-react";

const typeConfig = {
  info: { icon: Sparkles, color: "text-blue-500", bg: "bg-blue-50" },
  alert: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50" },
  wellness: { icon: Heart, color: "text-pink-500", bg: "bg-pink-50" },
  milestone: { icon: Trophy, color: "text-purple-500", bg: "bg-purple-50" },
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const [notifRes] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/generate`).catch(() => null),
      ]);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const markRead = async (id) => {
    await axios.put(`${API}/notifications/${id}/read`).catch(() => {});
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await axios.put(`${API}/notifications/read-all`).catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-[#faf8f5] transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-[#5B4956]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#C084A0] text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-in zoom-in duration-200">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 max-h-[420px] bg-white rounded-2xl shadow-2xl border border-[#E8E3DB] overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#F5EDE8]">
            <h3 className="font-semibold text-[#4A4A4A] text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#C084A0] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-[360px]">
            {notifications.length === 0 ? (
              <div className="py-12 text-center text-[#A1A1A1] text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const cfg = typeConfig[n.type] || typeConfig.info;
                const Icon = cfg.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#faf8f5] transition-colors border-b border-[#F5EDE8] last:border-0 ${!n.read ? "bg-[#FFF5EE]/50" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? "font-semibold text-[#4A4A4A]" : "text-[#5B4956]"}`}>{n.title}</p>
                      <p className="text-xs text-[#A1A1A1] mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-[#C0C0C0] mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#C084A0] flex-shrink-0 mt-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
